import { useMemo } from 'react';

import { useQuery } from '@apollo/client';

import type { Learner } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import type { CohortAudience } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import { AUDIENCE_LEARNERS_PAGE_SIZE } from 'bundles/enterprise-admin-messaging-v2/hooks/useAudienceLearners';
import EnterpriseLearnerCohortFetchDoc from 'bundles/enterprise-admin-messaging-v2/queries/EnterpriseLearnerCohortFetch.graphql';
import type {
  EnterpriseLearnerCohortFetchQuery,
  EnterpriseLearnerCohortFetchQueryVariables,
} from 'bundles/enterprise-admin-messaging-v2/queries/__generated__/EnterpriseLearnerCohortFetch';

type Params = {
  cohortAudience?: CohortAudience;
  page: number;
  pageSize?: number;
  search?: string;
  /** When true, skip the query entirely (used for virtual pages). */
  skip?: boolean;
};

/**
 * Data-layer hook wrapping the `fetchEnterpriseLearnerCohorts` query. Fetches a
 * page of learners from a previously-created NLQ cohort, with server-side
 * search via `searchString`.
 */
export const useCohortLearners = ({
  cohortAudience,
  page,
  pageSize = AUDIENCE_LEARNERS_PAGE_SIZE,
  search,
  skip,
}: Params) => {
  const { data, loading, error } = useQuery<
    EnterpriseLearnerCohortFetchQuery,
    EnterpriseLearnerCohortFetchQueryVariables
  >(EnterpriseLearnerCohortFetchDoc, {
    variables: {
      cohortId: cohortAudience?.cohortId ?? '',
      orgId: cohortAudience?.orgId ?? '',
      searchString: search || undefined,
      pageSize,
      pageToken: (page - 1) * pageSize,
    },
    context: { clientName: 'gatewayGql' },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    skip: !cohortAudience || skip,
  });

  const fetchResult = data?.EnterpriseLearnerCohort?.fetchEnterpriseLearnerCohorts;

  const learners: Learner[] = useMemo(
    () =>
      (fetchResult?.learners ?? []).map((l) => ({
        id: l.email ?? l.userId ?? '',
        name: l.fullName ?? '',
        email: l.email ?? '',
      })),
    [fetchResult]
  );

  const nextPageToken = fetchResult?.nextPageToken ?? 0;
  const hasMore = nextPageToken > 0;
  const cohortSize = fetchResult?.cohortSize ?? null;

  return { learners, nextPageToken, hasMore, cohortSize, loading, error: Boolean(error) };
};

export default useCohortLearners;
